import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
    Info, BarChart3, Archive, ArrowLeft, MapPin, Lock, Loader2, House
} from 'lucide-react';
import InfoTab from './InfoTab';
import ReadingsTab from './ReadingsTab';
import ArchiveTab from './ArchiveTab';

const Karat307Page = ({ device, onBack, isDesktop = false, onSelectDevice }) => {
    const [activeTab, setActiveTab] = useState('info');
    const [isScrolled, setIsScrolled] = useState(false);
    const [deviceData, setDeviceData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const scrollRef = useRef(null);

    // ✅ Безопасное получение имени устройства
    const deviceName = device?.name || device?.deviceName || '';

    const API_URL = process.env.REACT_APP_API_URL;

    useEffect(() => {
        if (deviceName) {
            loadDeviceData();
        }
    }, [deviceName]);

    const loadDeviceData = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');

            const response = await axios.get(
                `${API_URL}/api/devices/karat307/${deviceName}/info`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            setDeviceData(response.data);
        } catch (err) {
            console.error('Ошибка загрузки данных устройства:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleScroll = () => {
        if (scrollRef.current) {
            setIsScrolled(scrollRef.current.scrollTop > 10);
        }
    };

    const tabs = [
        { id: 'info', name: 'Информация', icon: Info },
        { id: 'readings', name: 'Показания', icon: BarChart3 },
        { id: 'archive', name: 'Архив', icon: Archive },
    ];

    // ✅ Формирование адреса из полей city, street, houseNumber
    const formatAddress = () => {
        const parts = [];
        if (device?.city) parts.push(device.city);
        if (device?.street) parts.push(device.street);
        if (device?.houseNumber) parts.push(`дом ${device.houseNumber}`);
        return parts.length > 0 ? parts.join(', ') : 'Адрес не указан';
    };

    // ✅ Получение статуса опломбирования (с fallback на device из props)
    const isOplombirovan = deviceData?.isOplombirovan ?? device?.isOplombirovan;
    const oplombirovanDate = deviceData?.oplombirovanDate ?? device?.oplombirovanDate;

    // ✅ Если deviceName пустой - не рендерим страницу
    if (!deviceName) {
        return (
            <div className="h-full flex items-center justify-center p-4">
                <div className="text-center">
                    <p className="text-slate-400">Устройство не выбрано</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col">
            {/* Шапка */}
            <div className="bg-slate-800 border-b border-slate-700 p-4 sticky top-0 z-10">
                {!isDesktop && (
                    <div className={`flex items-center gap-3 transition-all duration-300 overflow-hidden ${isScrolled ? 'max-h-0 mb-0 opacity-0' : 'max-h-20 mb-3 opacity-100'}`}>
                        <button
                            onClick={onBack}
                            className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition text-sm"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            К списку устройств
                        </button>
                    </div>
                )}

                <div className={`transition-all duration-300 overflow-hidden ${isScrolled ? 'max-h-0 opacity-0 mb-0' : 'max-h-60 opacity-100 mb-3'}`}>
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className="px-2 py-1 bg-green-600/20 text-green-400 rounded text-xs font-semibold">
                            Карат-307
                        </span>
                        {/* ✅ Бейдж опломбирования */}
                        {isOplombirovan && (
                            <span className="px-2 py-1 bg-green-600/20 text-green-400 rounded text-xs font-semibold flex items-center gap-1">
                                <Lock className="w-3 h-3" />
                                Опломбирован {oplombirovanDate && new Date(oplombirovanDate).toLocaleDateString('ru-RU')}
                            </span>
                        )}
                    </div>
                    <h2 className="text-lg sm:text-xl font-bold text-white">
                        Тепловычислитель Карат-307 {deviceName}
                    </h2>

                    {/* ✅ ОБНОВЛЕННЫЙ БЛОК АДРЕСА С КЛИКАБЕЛЬНЫМ MAP PIN */}
                    <div className="flex items-center gap-2 text-slate-400 text-sm mt-1">
                        <button
                            onClick={() => {
                                // 1. Сохраняем данные устройства как надежный источник
                                // Используем проп device, так как он гарантированно содержит deviceType, координаты и адрес
                                localStorage.setItem('switchPage', 'map');
                                localStorage.setItem('selectedDeviceOnMap', JSON.stringify(device));

                                // 2. Уведомляем Dashboard о необходимости переключить страницу
                                window.dispatchEvent(new CustomEvent('switchPageRequest'));

                                // 3. Уведомляем MapPage с небольшой задержкой, чтобы React успел отрендерить карту
                                setTimeout(() => {
                                    window.dispatchEvent(new CustomEvent('selectDeviceOnMap', { detail: { device: device } }));
                                }, 100);
                            }}
                            className="p-1 -ml-1 hover:bg-slate-700 hover:text-blue-400 rounded transition flex items-center gap-1 group"
                            title="Показать на карте"
                        >
                            <MapPin className="w-4 h-4 flex-shrink-0 group-hover:text-blue-400" />
                            <span className="group-hover:text-white transition truncate max-w-[200px] sm:max-w-md">
                                {formatAddress()}
                            </span>
                        </button>

                        {/* ✅ Кнопка перехода к дому */}
                        {onSelectDevice && device?.city && device?.street && device?.houseNumber && (
                            <button
                                onClick={() => {
                                    onSelectDevice({
                                        type: 'house',
                                        data: {
                                            city: device.city,
                                            street: device.street,
                                            house: `Дом ${device.houseNumber}`
                                        }
                                    });
                                }}
                                className="p-1 hover:bg-slate-700 hover:text-white rounded transition"
                                title="Перейти к дому"
                            >
                                <House className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>

                <div className="flex gap-1 overflow-x-auto custom-scrollbar-x-auto pb-1">
                    {tabs.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition flex-shrink-0 ${activeTab === tab.id
                                    ? 'bg-green-600 text-white'
                                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                                    }`}
                            >
                                <Icon className="w-4 h-4" />
                                <span>{tab.name}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Контент вкладок */}
            <div
                ref={scrollRef}
                onScroll={handleScroll}
                className="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-6"
            >
                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                            <Loader2 className="animate-spin h-8 w-8 text-green-500 mx-auto mb-3" />
                            <p className="text-slate-400 text-sm">Загрузка данных устройства...</p>
                        </div>
                    </div>
                ) : (
                    <>
                        {activeTab === 'info' && <InfoTab device={device} />}
                        {activeTab === 'readings' && <ReadingsTab deviceName={deviceName} />}
                        {activeTab === 'archive' && <ArchiveTab device={device} deviceName={deviceName} />}
                    </>
                )}
            </div>
        </div>
    );
};

export default Karat307Page;